import React, { useState, useEffect, useMemo } from 'react';
import {
  Container, Typography, Grid, Card, CardContent, Button, Stack, Box,
  Chip, IconButton, Paper, useTheme, Avatar, TextField, InputAdornment, LinearProgress
} from '@mui/material';
import {
  ArrowUpward, ArrowDownward,
  AccountBalance, Train, Security, Assignment, ElectricBolt,
  ArrowForward, ArrowBack, CheckCircle, Search, Star, School, EmojiEvents as Trophy
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useStudyPlans } from '../context/StudyPlansContext';
import { examsForCategory, CATEGORIES, EXAMS } from '../config/categories';
import { httpPost } from '../lib/http';
import ConfirmStudyPlan from '../components/ConfirmStudyPlan';

const DIFFICULTY_HOURS = {
  easy: 5,
  medium: 8,
  hard: 12
};

const CATEGORY_ICONS = {
  police: <Security fontSize="large" />,
  bank: <AccountBalance fontSize="large" />,
  ssc: <Assignment fontSize="large" />,
  railway: <Train fontSize="large" />,
  ntpc: <ElectricBolt fontSize="large" />,
};

const MotionBox = motion(Box);
const MotionCard = motion(Card);

export default function StudyPathSelector() {
  const theme = useTheme();
  const [step, setStep] = useState(1);
  const [category, setCategory] = useState('');
  const [selectedExam, setSelectedExam] = useState('');
  const [examSearch, setExamSearch] = useState('');
  const [subjects, setSubjects] = useState([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedContent, setSelectedContent] = useState({});
  const [expandedSubjects, setExpandedSubjects] = useState({});

  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const { upsertTrack } = useStudyPlans();

  const exams = useMemo(() => {
    if (!category) return [];
    const list = examsForCategory(category);
    if (!examSearch.trim()) return list;
    const q = examSearch.toLowerCase();
    return list.filter(e => e.label.toLowerCase().includes(q) || e.id.toLowerCase().includes(q));
  }, [category, examSearch]);

  useEffect(() => {
    if (!selectedExam) return setSubjects([]);

    const subs = EXAMS[selectedExam].subjects.map((s, i) => {
      const title = typeof s === 'string' ? s : s.title;
      return {
        title,
        difficulty: 'medium',
        estimatedHours: DIFFICULTY_HOURS.medium,
        order: i
      };
    });

    const prevPapersSubject = {
      title: 'Practice Previous Year Papers',
      difficulty: 'medium',
      estimatedHours: 6,
      order: subs.length
    };

    setSubjects([...subs, prevPapersSubject]);

    const contentMap = {};
    subs.forEach((sObj) => {
      const subjDef = (EXAMS[selectedExam].subjects || []).find(x => ((typeof x === 'string') ? x : x.title) === sObj.title);
      if (!subjDef || typeof subjDef === 'string') {
        contentMap[sObj.title] = { chapters: null, selected: true };
      } else {
        const chapters = {};
        (subjDef.chapters || []).forEach((ch) => {
          const topics = {};
          (ch.topics || []).forEach(t => { topics[t] = true; });
          chapters[ch.id] = { selected: true, topics };
        });
        contentMap[sObj.title] = { chapters, selected: true };
      }
    });
    contentMap[prevPapersSubject.title] = { chapters: null, selected: true };
    setSelectedContent(contentMap);
  }, [selectedExam]);

  const toggleExpand = (subjectTitle) => {
    setExpandedSubjects(prev => ({ ...prev, [subjectTitle]: !prev[subjectTitle] }));
  };

  const toggleChapter = (subjectTitle, chapterId) => {
    setSelectedContent(prev => {
      const copy = { ...prev };
      const subj = copy[subjectTitle];
      if (!subj || !subj.chapters) return prev;
      const chaptersCopy = { ...subj.chapters };
      const ch = chaptersCopy[chapterId];
      if (!ch) return prev;
      const topicsCopy = { ...ch.topics };
      const chCopy = { ...ch, topics: topicsCopy, selected: !ch.selected };
      if (chCopy.selected) {
        Object.keys(topicsCopy).forEach(t => { topicsCopy[t] = true; });
      }
      chaptersCopy[chapterId] = chCopy;
      const subjCopy = { ...subj, chapters: chaptersCopy };
      copy[subjectTitle] = subjCopy;
      return copy;
    });
  };

  const toggleTopic = (subjectTitle, chapterId, topic) => {
    setSelectedContent(prev => {
      const copy = { ...prev };
      const subj = copy[subjectTitle];
      if (!subj || !subj.chapters) return prev;
      const chaptersCopy = { ...subj.chapters };
      const ch = chaptersCopy[chapterId];
      if (!ch) return prev;
      const topicsCopy = { ...ch.topics };
      topicsCopy[topic] = !Boolean(topicsCopy[topic]);
      const chSelected = !Object.values(topicsCopy).some(v => !v);
      const chCopy = { ...ch, topics: topicsCopy, selected: chSelected };
      chaptersCopy[chapterId] = chCopy;
      const subjCopy = { ...subj, chapters: chaptersCopy };
      copy[subjectTitle] = subjCopy;
      return copy;
    });
  };

  const moveSubject = (index, dir) => {
    const copy = [...subjects];
    const target = index + dir;
    if (target < 0 || target >= copy.length) return;
    [copy[index], copy[target]] = [copy[target], copy[index]];
    setSubjects(copy.map((s, i) => ({ ...s, order: i })));
  };

  const updateDifficulty = (index, difficulty) => {
    const copy = [...subjects];
    copy[index] = {
      ...copy[index],
      difficulty,
      estimatedHours: DIFFICULTY_HOURS[difficulty]
    };
    setSubjects(copy);
  };

  const confirmAndSave = async () => {
    if (!isAuthenticated || !selectedExam) return;
    if (saving) return;

    setSaving(true);
    const userId = user?.id || user?.userId || user?._id;
    const subjectsPayload = subjects.map((s, i) => {
      const subjDef = (EXAMS[selectedExam].subjects || []).find(x => ((typeof x === 'string') ? x : x.title) === s.title);
      const sel = selectedContent[s.title];
      if (!subjDef || typeof subjDef === 'string' || !sel || !sel.chapters) {
        return { title: s.title, difficulty: s.difficulty, estimatedHours: s.estimatedHours, order: s.order, chapters: null };
      }
      const chapters = (subjDef.chapters || []).map(ch => {
        const chSel = sel.chapters?.[ch.id] || { selected: false, topics: {} };
        return {
          id: ch.id,
          title: ch.title,
          selected: Boolean(chSel.selected),
          topics: (ch.topics || []).map(t => ({ title: t, selected: Boolean(chSel.topics?.[t]) }))
        };
      });
      return { title: s.title, difficulty: s.difficulty, estimatedHours: s.estimatedHours, order: s.order, chapters };
    });

    const perSubjectTasks = [];
    subjectsPayload.forEach((s, si) => {
      if (!s.chapters) {
        perSubjectTasks.push({ id: `lesson-subject-${si}`, type: 'lesson', title: s.title, subject: s.title });
      } else {
        s.chapters.forEach((ch, ci) => {
          if (!ch.selected && ch.topics && ch.topics.some(t => t.selected)) {
            ch.topics.forEach((t, ti) => {
              if (t.selected) perSubjectTasks.push({ id: `lesson-${si}-${ci}-${ti}`, type: 'lesson', title: `${s.title} - ${ch.title} - ${t.title}`, subject: s.title, chapterId: ch.id, topic: t.title });
            });
          } else if (ch.selected) {
            perSubjectTasks.push({ id: `lesson-${si}-${ci}`, type: 'lesson', title: `${s.title} - ${ch.title}`, subject: s.title, chapterId: ch.id });
          }
        });
      }
      perSubjectTasks.push({ id: `genai-${si}`, type: 'genai_test', title: `Test Your Skill With GenAI - ${s.title}`, subject: s.title });
      perSubjectTasks.push({ id: `prev-${si}`, type: 'previous_papers', title: `${s.title} - Previous Year Papers`, subject: s.title });
    });

    const finalPractice = { id: `final-prevpapers`, type: 'previous_papers', title: 'Practice Previous Year Papers - Full Revision' };
    const tasks = [...perSubjectTasks, finalPractice];

    const payload = {
      pathId: selectedExam,
      category,
      title: `${(EXAMS[selectedExam] && EXAMS[selectedExam].label) || selectedExam} Study Track`,
      subjects: subjectsPayload,
      tasks,
      totalEstimatedHours: subjects.reduce((a, b) => a + b.estimatedHours, 0),
      createdAt: new Date().toISOString()
    };

    try {
      const res = await httpPost(`/api/users/${encodeURIComponent(userId)}/study-tracks`, payload);
      let data = null;
      try { data = await res.json(); } catch (e) {}
      const track = data?.track || data?.data || (data && typeof data === 'object' ? data : null);
      if (track && upsertTrack) upsertTrack(track);
      const trackId = (track && (track.id || track.pathId)) || data?.id || data?.track?.id || null;
      setSaving(false);
      setPreviewOpen(false);
      if (trackId) {
        window.location.href = `/study-paths/${encodeURIComponent(trackId)}`;
      } else {
        navigate(`/study-paths/${selectedExam}`);
      }
    } catch (err) {
      setSaving(false);
      console.error('Failed saving study track', err);
    }
  };

  const nextStep = () => setStep(s => Math.min(s + 1, 3));
  const prevStep = () => setStep(s => Math.max(s - 1, 1));

  const containerVariants = {
    hidden: { opacity: 0, y: 15 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.35 } },
    exit: { opacity: 0, y: -15, transition: { duration: 0.25 } }
  };

  const progressPercent = step === 1 ? 33 : step === 2 ? 66 : 100;

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 4, md: 7 } }}>
      <Stack spacing={4}>
        {/* World-Class Stepper Header */}
        <Box sx={{ textAlign: 'center', mb: 2 }}>
          <Chip
            label={`Step ${step} of 3 • ${step === 1 ? 'Goal Selection' : step === 2 ? 'Exam Selection' : 'Customization'}`}
            sx={{
              bgcolor: 'rgba(75, 0, 130, 0.08)',
              color: '#4B0082',
              fontWeight: 800,
              px: 1.5,
              py: 0.5,
              mb: 2,
              borderRadius: 2
            }}
          />
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: '2rem', md: '2.8rem' },
              fontWeight: 900,
              color: '#11181C',
              letterSpacing: '-0.02em',
              mb: 1
            }}
          >
            {step === 1 ? 'Select Your Study Category' : step === 2 ? 'Choose Your Target Exam' : 'Customize Your AI Study Track'}
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 640, mx: 'auto', fontWeight: 400 }}>
            {step === 1
              ? 'Select your academic domain to generate personalized syllabus modules and practice tracks.'
              : step === 2
              ? 'Pick your target exam to load syllabus topics, previous year papers, and AI mock tests.'
              : 'Review, reorder, and adjust subject prep hours before launching your personal study dashboard.'}
          </Typography>

          {/* Linear Progress Bar */}
          <Box sx={{ width: '100%', maxWidth: 500, mx: 'auto', mt: 3 }}>
            <LinearProgress
              variant="determinate"
              value={progressPercent}
              sx={{
                height: 8,
                borderRadius: 4,
                bgcolor: 'rgba(75, 0, 130, 0.08)',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 4,
                  backgroundColor: '#4B0082'
                }
              }}
            />
          </Box>
        </Box>

        <AnimatePresence mode="wait">
          {/* STEP 1: CATEGORY SELECTION */}
          {step === 1 && (
            <MotionBox
              key="step1"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
            >
              <Grid container spacing={3} justifyContent="center" alignItems="stretch">
                {CATEGORIES.map((cat) => {
                  const examCount = examsForCategory(cat.id).length;
                  const isSelected = category === cat.id;
                  return (
                    <Grid item xs={12} sm={6} md={4} key={cat.id} style={{ display: 'flex' }}>
                      <MotionCard
                        whileHover={{ translateY: -6, boxShadow: '0 20px 40px rgba(75, 0, 130, 0.16)' }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => { setCategory(cat.id); setExamSearch(''); setSelectedExam(''); nextStep(); }}
                        sx={{
                          cursor: 'pointer',
                          width: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          borderRadius: 3,
                          border: isSelected ? '2px solid #4B0082' : '1px solid rgba(75, 0, 130, 0.12)',
                          backgroundColor: isSelected ? 'rgba(75, 0, 130, 0.04)' : '#FFFFFF',
                          transition: 'all 0.3s ease',
                          boxShadow: isSelected ? '0 16px 36px rgba(75, 0, 130, 0.15)' : '0 8px 24px rgba(15, 23, 42, 0.04)'
                        }}
                      >
                        <CardContent sx={{ p: 3.5, display: 'flex', flexDirection: 'column', flex: 1, textAlign: 'center', alignItems: 'center' }}>
                          <Avatar
                            sx={{
                              width: 72,
                              height: 72,
                              mb: 2.5,
                              bgcolor: isSelected ? '#4B0082' : 'rgba(75, 0, 130, 0.08)',
                              color: isSelected ? '#FFD700' : '#4B0082',
                              boxShadow: '0 8px 20px rgba(75, 0, 130, 0.12)'
                            }}
                          >
                            {CATEGORY_ICONS[cat.id] || <School fontSize="large" />}
                          </Avatar>
                          <Typography variant="h5" fontWeight={800} sx={{ color: '#11181C', mb: 1 }}>
                            {cat.label}
                          </Typography>
                          <Typography color="text.secondary" sx={{ fontSize: '0.92rem', lineHeight: 1.5, flex: 1, mb: 2 }}>
                            Structured prep, previous year papers & AI mocks for {cat.label}.
                          </Typography>
                          <Chip
                            label={`${examCount} Exams Available`}
                            size="small"
                            sx={{
                              bgcolor: 'rgba(75, 0, 130, 0.06)',
                              color: '#4B0082',
                              fontWeight: 700,
                              fontSize: '0.78rem'
                            }}
                          />
                        </CardContent>
                      </MotionCard>
                    </Grid>
                  );
                })}
              </Grid>
            </MotionBox>
          )}

          {/* STEP 2: EXAM SELECTION */}
          {step === 2 && (
            <MotionBox
              key="step2"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
            >
              {/* Step 2 Toolbar: Back button + Search bar */}
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  mb: 3.5,
                  borderRadius: 3,
                  border: '1px solid rgba(75, 0, 130, 0.12)',
                  bgcolor: '#FFFFFF',
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: 'center',
                  justify: 'space-between',
                  gap: 2
                }}
              >
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Button
                    variant="outlined"
                    startIcon={<ArrowBack />}
                    onClick={prevStep}
                    sx={{
                      color: '#4B0082',
                      borderColor: '#4B0082',
                      fontWeight: 700,
                      borderRadius: 2,
                      '&:hover': { bgcolor: 'rgba(75, 0, 130, 0.04)' }
                    }}
                  >
                    Back to Categories
                  </Button>
                  <Chip
                    label={CATEGORIES.find(c => c.id === category)?.label || 'Category'}
                    sx={{ bgcolor: '#4B0082', color: '#FFFFFF', fontWeight: 800 }}
                  />
                </Stack>

                <TextField
                  placeholder="Search exam name (e.g. UPP, CAPF)..."
                  size="small"
                  value={examSearch}
                  onChange={(e) => setExamSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search sx={{ color: '#4B0082' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ width: { xs: '100%', sm: 300 }, ml: 'auto' }}
                />
              </Paper>

              <Grid container spacing={2.5} alignItems="stretch">
                {exams.map((ex) => {
                  const isSelected = selectedExam === ex.id;
                  return (
                    <Grid item xs={12} sm={6} md={4} key={ex.id} style={{ display: 'flex' }}>
                      <MotionCard
                        whileHover={{ translateY: -4, boxShadow: '0 16px 32px rgba(75, 0, 130, 0.14)' }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => { setSelectedExam(ex.id); nextStep(); }}
                        sx={{
                          cursor: 'pointer',
                          width: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          borderRadius: 3,
                          border: isSelected ? '2px solid #4B0082' : '1px solid rgba(75, 0, 130, 0.12)',
                          backgroundColor: isSelected ? 'rgba(75, 0, 130, 0.04)' : '#FFFFFF',
                          transition: 'all 0.25s ease',
                          boxShadow: '0 6px 18px rgba(15, 23, 42, 0.04)'
                        }}
                      >
                        <CardContent sx={{ p: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flex: 1 }}>
                          <Stack direction="row" spacing={1.5} alignItems="center">
                            <Avatar sx={{ bgcolor: isSelected ? '#4B0082' : 'rgba(75, 0, 130, 0.08)', color: isSelected ? '#FFD700' : '#4B0082', width: 40, height: 40 }}>
                              <Trophy fontSize="small" />
                            </Avatar>
                            <Typography variant="h6" fontWeight={800} sx={{ fontSize: '1.05rem', color: '#11181C' }}>
                              {ex.label}
                            </Typography>
                          </Stack>
                          {isSelected ? (
                            <CheckCircle sx={{ color: '#4B0082' }} />
                          ) : (
                            <ArrowForward sx={{ color: '#687076' }} />
                          )}
                        </CardContent>
                      </MotionCard>
                    </Grid>
                  );
                })}
              </Grid>
            </MotionBox>
          )}

          {/* STEP 3: CUSTOMIZATION & CONFIRMATION */}
          {step === 3 && (
            <MotionBox
              key="step3"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
            >
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  mb: 3.5,
                  borderRadius: 3,
                  border: '1px solid rgba(75, 0, 130, 0.12)',
                  bgcolor: '#FFFFFF',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2
                }}
              >
                <Button
                  variant="outlined"
                  startIcon={<ArrowBack />}
                  onClick={prevStep}
                  sx={{
                    color: '#4B0082',
                    borderColor: '#4B0082',
                    fontWeight: 700,
                    borderRadius: 2,
                    '&:hover': { bgcolor: 'rgba(75, 0, 130, 0.04)' }
                  }}
                >
                  Back to Exams
                </Button>
                <Chip
                  label={EXAMS[selectedExam]?.label || selectedExam}
                  sx={{ bgcolor: '#4B0082', color: '#FFFFFF', fontWeight: 800 }}
                />
              </Paper>

              <Grid container spacing={3.5}>
                <Grid item xs={12} md={8}>
                  <Stack spacing={2}>
                    {subjects.map((s, i) => (
                      <Paper
                        key={s.title}
                        elevation={0}
                        sx={{
                          p: 2.8,
                          borderRadius: 3,
                          border: '1px solid rgba(75, 0, 130, 0.12)',
                          bgcolor: '#FFFFFF',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                          transition: 'all 0.2s ease',
                          '&:hover': { boxShadow: '0 12px 30px rgba(75, 0, 130, 0.08)' }
                        }}
                      >
                        <Avatar sx={{ bgcolor: '#4B0082', color: '#FFD700', fontWeight: 800, width: 42, height: 42 }}>
                          {i + 1}
                        </Avatar>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="h6" fontWeight={800} sx={{ color: '#11181C' }}>{s.title}</Typography>
                          <Stack direction="row" spacing={1} sx={{ mt: 0.8 }} alignItems="center">
                            <Chip
                              label={s.difficulty.toUpperCase()}
                              size="small"
                              color={s.difficulty === 'hard' ? 'error' : s.difficulty === 'medium' ? 'warning' : 'success'}
                              sx={{ fontWeight: 800, fontSize: '0.68rem', borderRadius: 1.5 }}
                            />
                            <Typography variant="body2" color="text.secondary" fontWeight={500}>
                              {s.estimatedHours} Estimated Hours
                            </Typography>
                          </Stack>
                        </Box>
                        <Stack spacing={0.5}>
                          <IconButton size="small" onClick={() => moveSubject(i, -1)} disabled={i === 0}>
                            <ArrowUpward fontSize="small" />
                          </IconButton>
                          <IconButton size="small" onClick={() => moveSubject(i, 1)} disabled={i === subjects.length - 1}>
                            <ArrowDownward fontSize="small" />
                          </IconButton>
                        </Stack>
                      </Paper>
                    ))}
                  </Stack>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Card
                    sx={{
                      borderRadius: 3,
                      bgcolor: '#FFFFFF',
                      border: '1px solid rgba(75, 0, 130, 0.16)',
                      boxShadow: '0 16px 40px rgba(75, 0, 130, 0.08)'
                    }}
                  >
                    <CardContent sx={{ p: 3.5 }}>
                      <Typography variant="h5" fontWeight={900} sx={{ color: '#4B0082', mb: 2 }}>
                        Track Summary
                      </Typography>
                      <Stack spacing={2}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1, borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                          <Typography color="text.secondary" fontWeight={500}>Selected Exam</Typography>
                          <Typography fontWeight={800}>{EXAMS[selectedExam]?.label || selectedExam}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1, borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                          <Typography color="text.secondary" fontWeight={500}>Total Subjects</Typography>
                          <Typography fontWeight={800}>{subjects.length}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1 }}>
                          <Typography color="text.secondary" fontWeight={500}>Est. Prep Time</Typography>
                          <Typography fontWeight={800} color="primary.main">
                            {subjects.reduce((a, b) => a + b.estimatedHours, 0)} Hours
                          </Typography>
                        </Box>
                        <Button
                          variant="contained"
                          fullWidth
                          size="large"
                          onClick={() => setPreviewOpen(true)}
                          startIcon={<Star />}
                          sx={{
                            mt: 2,
                            py: 1.6,
                            borderRadius: 3,
                            fontWeight: 800,
                            fontSize: '1rem',
                            bgcolor: '#4B0082',
                            color: '#FFFFFF',
                            boxShadow: '0 10px 25px rgba(75, 0, 130, 0.25)',
                            '&:hover': { bgcolor: '#380062' }
                          }}
                        >
                          Preview & Launch Track
                        </Button>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </MotionBox>
          )}
        </AnimatePresence>
      </Stack>

      <ConfirmStudyPlan
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        subjects={subjects}
        selectedContent={selectedContent}
        expandedSubjects={expandedSubjects}
        toggleExpand={toggleExpand}
        toggleChapter={toggleChapter}
        toggleTopic={toggleTopic}
        confirmAndSave={confirmAndSave}
        saving={saving}
        selectedExam={selectedExam}
      />
    </Container>
  );
}
